<template>
  <div class="tree">
    <p v-if="interactive" class="counter-band">Which technology? Bright ones are reachable now.</p>

    <!-- One part of the tree at a time, and what you tapped at the TOP
         (Seth, playtest 9: Statecraft and the Arts underneath "make
         everything fall down really far", and Granaries' details were "all
         the way down at the bottom"). -->
    <div v-if="groups.length > 1" class="tree__tabs" role="tablist">
      <button v-for="g in groups" :key="g.key" class="tree__tab" :class="{ 'tree__tab--on': g.key === shownKey }" @click="pickGroup(g.key)">{{ g.short }}</button>
    </div>
    <!-- What a node is, on tap: the text lives here instead of on every box. -->
    <div v-if="detail" class="node-detail">
      <div class="node-detail__head">
        <strong>{{ detail.name }}</strong>
        <span class="node-detail__state">{{ detail.state === 'known' ? 'known' : detail.state === 'reachable' && !gateFor(detail) ? 'reachable now' : 'not available yet' }}</span>
      </div>
      <!-- What it takes to research it comes first and big: it is the one
           thing the Scholars act on (playtest AvSCF). -->
      <span v-if="detail.state !== 'known'" class="node-detail__needs">
        {{ needLead(detail) }}
        <span class="needs">
          <template v-for="(b, i) in detail.needBadges" :key="b.aspect">{{ joiner(i, detail.needBadges.length) }}<Aspect :aspect="b.aspect" :value="b.value" /></template>
        </span>
      </span>
      <span class="node-detail__text">{{ detail.text }}</span>
      <span class="node-detail__line">for {{ techForText(detail.id, (r) => ROLE_INFO[r].label) }}<template v-if="detail.state !== 'known'"> · costs {{ techPrice(civ, detail.id) }} knowledge to learn</template></span>
      <span v-if="detail.state === 'locked' && detail.needs.length" class="node-detail__line node-detail__line--locked">
        first: {{ detail.needs.map((n) => TECHS[n].name).join(' + ') }}
      </span>
      <span v-if="detail.state !== 'known' && gateFor(detail)" class="node-detail__line node-detail__line--locked">{{ gateFor(detail) }}</span>
      <!-- From the tree straight to the bench (playtest 9: "the tree tells
           you what Tracking does but doesn't aim it"). -->
      <!-- Not while a gate still stands (playtest 50qbf: a deep technology
           offered Research with no deep bench open). -->
      <button v-if="aimable && detail.state === 'reachable' && !gateFor(detail)" class="primary node-detail__aim" @click="$emit('aim', detail.id); open = null">{{ onBench(detail.tier) ? `Research ${detail.name}` : `To the ${machineForTier(detail.tier)?.name}` }}</button>
    </div>

    <section v-for="g in shownGroups" :key="g.key" class="tree__group">
      <h4 class="tree__group-title">{{ g.title }}</h4>
      <div class="tree__scroll">
        <div class="tree__canvas" :style="{ width: `${g.width}px`, height: `${g.height}px` }">
          <!-- the branches themselves: a line from each prerequisite -->
          <svg class="tree__wires" :viewBox="`0 0 ${g.width} ${g.height}`" aria-hidden="true">
            <path v-for="(e, i) in g.edges" :key="i" :d="e.d" :class="{ 'tree__wire--live': e.live }" />
          </svg>
          <component
            :is="interactive && n.reachable ? 'button' : 'div'"
            v-for="n in g.nodes"
            :key="n.id"
            class="node"
            :class="[`node--${n.state}`, { 'node--open': open === n.id }]"
            :style="{ left: `${n.x}px`, top: `${n.y}px` }"
            @click="tap(n)"
          >
            <span class="node__name">{{ n.name }}</span>
            <span v-if="n.state === 'known'" class="node__tick" aria-hidden="true">✓</span>
            <span v-else-if="n.needBadges.length" class="node__needs">
              <Aspect v-for="b in n.needBadges" :key="b.aspect" :aspect="b.aspect" :value="b.value" :word="false" />
            </span>
          </component>
        </div>
      </div>
    </section>

    <p class="tree__key">
      <span class="node node--known node--key"><span class="node__name">known</span></span>
      <span class="node node--reachable node--key"><span class="node__name">reachable</span></span>
      <span class="node node--locked node--key"><span class="node__name">locked</span></span>
      <span class="tree__key-wire">a wire: known first</span>
    </p>

    <p v-if="undiscovered" class="tree__undiscovered">
      {{ undiscovered }} {{ undiscovered === 1 ? 'branch' : 'branches' }} still undiscovered.
    </p>
  </div>
</template>

<script>
import { ROLE_INFO } from '@/engine/constants';
import {
  TECHS, TECH_TIERS, techReachable, techVisible, BRANCHES, BRANCH_ORDER,
  techForText,
} from '@/engine/cards';
import { techPrice } from '@/engine/roleActions';
import { loreOf, ASPECTS } from '@/engine/lore';
import { machineForTier, onBench } from '@/engine/machines';
import { machineStands } from '@/engine/city';
import Aspect from './Aspect.vue';

// The tree, drawn as a tree. Matt, 2026-09-19: "it doesn't look like a
// tree, it just looks like a list - let's make that into some kind of a
// better graphical representation."
//
// Tiers are rows, prerequisites are wires, and a node carries its name and
// the properties its reaction needs - nothing else. Everything wordy waits
// behind a tap, which is what keeps the page from being a wall of text.
const W = 88;
const H = 44;
const GAP_X = 10;
const GAP_Y = 18;

export default {
  name: 'TechTree',
  components: { Aspect },
  props: {
    civ: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    // A reachable node's detail offers to aim the bench at it.
    aimable: { type: Boolean, default: false },
  },
  emits: ['pick', 'aim'],
  data() { return { open: null, group: 'trunk', TECHS, ROLE_INFO }; },
  computed: {
    // The trunk, then every branch somebody has opened. Branches nobody has
    // found are not drawn: they are discovered, not read.
    groups() {
      const known = new Set(this.civ.techs || []);
      const reactions = loreOf(this.civ)?.reactions || {};
      const entry = (id, t) => {
        const need = reactions[id] || {};
        return {
          id,
          ...t,
          state: known.has(id) ? 'known' : techReachable(this.civ, id) ? 'reachable' : 'locked',
          reachable: techReachable(this.civ, id),
          needBadges: ASPECTS.filter((k) => k in need).map((k) => ({ aspect: k, value: need[k] })),
        };
      };
      const laid = (nodes, title, key, short) => {
        const tiers = [...new Set(nodes.map((n) => n.tier))].sort((a, b) => a - b);
        const rows = tiers.map((tier) => nodes.filter((n) => n.tier === tier));
        // Each row is ordered by where its prerequisites sit on the row
        // above (a barycentre sort), so the wires run mostly straight down
        // instead of crossing the whole tree.
        const columnOf = {};
        rows.forEach((row, r) => {
          if (r > 0) {
            row.sort((a, b) => {
              const mid = (n) => {
                const cols = (n.needs || []).map((id) => columnOf[id]).filter((c) => c !== undefined);
                return cols.length ? cols.reduce((x, y) => x + y, 0) / cols.length : 99;
              };
              return mid(a) - mid(b);
            });
          }
          row.forEach((n, c) => { columnOf[n.id] = c; });
        });
        const widest = Math.max(...rows.map((r) => r.length));
        const placed = [];
        rows.forEach((row, r) => {
          // Short rows are centred under the widest one.
          const offset = ((widest - row.length) * (W + GAP_X)) / 2;
          row.forEach((n, c) => placed.push({ ...n, x: offset + c * (W + GAP_X), y: r * (H + GAP_Y) }));
        });
        const at = Object.fromEntries(placed.map((n) => [n.id, n]));
        const edges = [];
        placed.forEach((n) => {
          (n.needs || []).forEach((needed) => {
            const from = at[needed];
            if (!from) return;
            const x1 = from.x + W / 2;
            const y1 = from.y + H;
            const x2 = n.x + W / 2;
            const y2 = n.y;
            const mid = (y1 + y2) / 2;
            edges.push({
              d: `M ${x1} ${y1} C ${x1} ${mid}, ${x2} ${mid}, ${x2} ${y2}`,
              live: from.state === 'known',
            });
          });
        });
        const width = widest * (W + GAP_X) - GAP_X;
        const height = rows.length * (H + GAP_Y) - GAP_Y;
        return {
          key, title, short, nodes: placed, edges, width, height,
        };
      };

      const trunk = TECH_TIERS.flatMap((tier) => Object.entries(TECHS)
        .filter(([, t]) => !t.branch && t.tier === tier)
        .map(([id, t]) => entry(id, t)));
      const groups = [laid(trunk, `The trunk · ${trunk.filter((n) => n.state === 'known').length} of ${trunk.length} known`, 'trunk', 'The trunk')];
      BRANCH_ORDER
        .filter((b) => (this.civ.branches || []).includes(b))
        .forEach((b) => {
          const nodes = Object.entries(TECHS)
            .filter(([id, t]) => t.branch === b && techVisible(this.civ, id))
            .map(([id, t]) => entry(id, t));
          if (nodes.length) groups.push(laid(nodes, `${BRANCHES[b].name} · a branch`, `branch-${b}`, BRANCHES[b].name));
        });
      return groups;
    },
    shownKey() { return this.groups.some((g) => g.key === this.group) ? this.group : 'trunk'; },
    shownGroups() { return this.groups.filter((g) => g.key === this.shownKey); },
    detail() {
      return this.groups.flatMap((g) => g.nodes).find((n) => n.id === this.open) || null;
    },
    undiscovered() { return BRANCH_ORDER.length - (this.civ.branches || []).filter((b) => BRANCHES[b]).length; },
  },
  methods: {
    techForText,
    techPrice,
    joiner(i, n) {
      if (i === 0) return '';
      if (n === 2) return ' and ';
      return i === n - 1 ? ', and ' : ', ';
    },
    pickGroup(key) { this.group = key; this.open = null; },
    onBench,
    machineForTier,
    needLead(n) {
      const m = onBench(n.tier) ? null : machineForTier(n.tier);
      return m ? `needs a ${m.product} that is` : 'needs two principles that are both';
    },
    // What still stands between this one and being made: above tier 2,
    // its machine (the ladder, 2026-10-15).
    gateFor(n) {
      const tier = n.tier || 1;
      if (onBench(tier)) return '';
      const m = machineForTier(tier);
      if (m && !machineStands(this.civ, m.id)) return `made in the ${m.name}: learn ${TECHS[m.tech]?.name || m.tech}, then the Stewards build one`;
      return '';
    },
    tap(n) {
      if (this.interactive && n.reachable) { this.$emit('pick', n.id); return; }
      // ONE PLACE per technology (playtest 11, Matt: the same technology was
      // described "in the bench, on the tile I clicked, and in the tree...
      // it needs to just be all in one place"). Within reach: tapping it in
      // the tree aims the bench at it, and the glass describes it. Out of
      // reach, the tree's own panel is the only place it is described.
      // ...and within reach too, now: tapping one used to aim at once and
      // jump to the bench before saying what it does (playtest, 2026-10-15,
      // Matt: "isn't showing me what it does before I use it"). The panel
      // says, and its Research button aims.
      this.open = this.open === n.id ? null : n.id;
    },
  },
};
</script>
